import React from 'react'
import { useTranslation } from 'react-i18next'

import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import { PanelContainer } from '../../../../components/Panel'
import { WebEdiDto } from '../../../../data/models'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import { stringToDate } from '../../../../utils/helper/stringToDate'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { useDespatchAdviceById } from '../../data'

interface Props {
  id: WebEdiDto['Id']
}

export const OrderInformation: React.FC<Props> = ({ id }) => {
  const { data, isLoading } = useDespatchAdviceById(id)
  const { i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  // ************************************************************
  // components
  // ************************************************************
  // orderId
  const orderIdLabel = <label className="text-sm" htmlFor="orderId"></label>

  const orderIdComponent = (
    <InputText
      id="orderId"
      value={data?.Payload.OrderId}
      className="w-full"
      disabled
    />
  )

  const orderIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={orderIdComponent}
      label={orderIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDate
  const orderDateLabel = <label className="text-sm" htmlFor="orderDate"></label>

  const orderDateComponent = (
    <Calendar
      id="orderDate"
      value={stringToDate(`${data?.Payload.OrderDate}`)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={orderDateComponent}
      label={orderDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderDeliveryDate
  const orderDeliveryDateLabel = (
    <label className="text-sm" htmlFor="orderDeliveryDate"></label>
  )

  const orderDeliveryDateComponent = (
    <Calendar
      id="orderDeliveryDate"
      value={stringToDate(`${data?.Payload.OrderDeliveryDate}`)}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={orderDeliveryDateComponent}
      label={orderDeliveryDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // termsOfDelivery
  const termsOfDeliveryLabel = (
    <label className="text-sm" htmlFor="termsOfDelivery"></label>
  )

  const termsOfDeliveryComponent = (
    <div id="termsOfDelivery" className="p-inputgroup">
      <span className="p-inputgroup-addon">
        {data?.Payload.TermsOfDelivery?.Code}
      </span>
      <InputText
        value={data?.Payload.TermsOfDelivery?.Description}
        className="w-full"
        disabled
      />
    </div>
  )

  const termsOfDeliveryWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={termsOfDeliveryComponent}
      label={termsOfDeliveryLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderCurrency
  const orderCurrencyLabel = (
    <label className="text-sm" htmlFor="orderCurrency"></label>
  )

  const orderCurrencyComponent = (
    <InputText
      id="orderCurrency"
      value={data?.Payload.OrderCurrency}
      className="w-full"
      disabled
    />
  )

  const orderCurrencyWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={orderCurrencyComponent}
      label={orderCurrencyLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationTotalNetItemAmount
  const confirmationTotalNetItemAmountLabel = (
    <label className="text-sm" htmlFor="confirmationTotalNetItemAmount"></label>
  )

  const confirmationTotalNetItemAmountComponent = (
    <InputNumber
      id="confirmationTotalNetItemAmount"
      value={data?.Payload.ConfirmationTotalNetItemAmount}
      mode="currency"
      currency={data?.Payload.OrderCurrency}
      locale={language}
      className="w-full"
      disabled
    />
  )

  const confirmationTotalNetItemAmountWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={confirmationTotalNetItemAmountComponent}
      label={confirmationTotalNetItemAmountLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // orderNotes
  const orderNotesLabel = (
    <label className="text-sm" htmlFor="orderNotes"></label>
  )

  const orderNotesComponent = (
    <InputTextarea
      id="orderNotes"
      value={data?.Payload.OrderNotes}
      rows={5}
      className="w-full"
      disabled
    />
  )

  const orderNotesWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.orderInformation}
      component={orderNotesComponent}
      label={orderNotesLabel}
      className="col-12 md:col-12 lg:col-12 xl:col-9"
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      header={getConfigTranslation(
        config.despatchAdvice.orderInformation.title,
      )}
      id="despatchAdvice-orderInformation"
      visible={config.despatchAdvice.orderInformation.enabled}
    >
      <>
        <div className="formgrid grid">
          {/* orderId */}
          {orderIdWrapper}

          {/* orderDate */}
          {orderDateWrapper}

          {/* orderDeliveryDate */}
          {orderDeliveryDateWrapper}

          {/* termsOfDelivery */}
          {termsOfDeliveryWrapper}

          {/* orderCurrency */}
          {orderCurrencyWrapper}

          {/* confirmationTotalNetItemAmount */}
          {confirmationTotalNetItemAmountWrapper}

          {/* orderNotes */}
          {orderNotesWrapper}
        </div>
      </>
    </PanelContainer>
  )
}
